import React, { useState, useEffect, useRef } from "react";
import { db } from "../firebase";
import { collection, getDocs, orderBy, query, deleteDoc, doc, updateDoc } from "firebase/firestore";
import logo from "../Assets/TB_Energy_Logo.webp";
import "./AdminPage.css";

const ADMIN_PASSWORD = "techb@admin2026";

// ── Inline comment cell ───────────────────────────────────────────────────────
const CommentCell = ({ id, collectionName, value, saving, onSave }) => {
  const [text, setText] = useState(value);
  const [dirty, setDirty] = useState(false);
  // Refs so blur/save callbacks always see latest values
  const textRef = useRef(value);
  const dirtyRef = useRef(false);

  useEffect(() => { textRef.current = text; }, [text]);
  useEffect(() => { dirtyRef.current = dirty; }, [dirty]);

  // Sync when parent refreshes data
  useEffect(() => {
    setText(value);
    setDirty(false);
    textRef.current = value;
    dirtyRef.current = false;
  }, [value]);

  const doSave = () => {
    if (dirtyRef.current) {
      onSave(collectionName, id, textRef.current);
      setDirty(false);
      dirtyRef.current = false;
    }
  };

  const handleChange = (e) => {
    setText(e.target.value);
    setDirty(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); doSave(); e.target.blur(); }
    if (e.key === "Escape") {
      setText(value);
      setDirty(false);
      textRef.current = value;
      dirtyRef.current = false;
      e.target.blur();
    }
  };

  return (
    <div className="admin__comment-wrap">
      <div className="admin__comment-row">
        <input
          className={`admin__comment-input ${dirty ? "unsaved" : ""} ${saving ? "saving" : ""}`}
          value={text}
          placeholder="Add note…"
          onChange={handleChange}
          onBlur={doSave}
          onKeyDown={handleKeyDown}
          disabled={saving}
        />
        {dirty && !saving && (
          <button
            className="admin__comment-save-btn"
            onMouseDown={(e) => { e.preventDefault(); doSave(); }}
            onTouchEnd={(e) => { e.preventDefault(); doSave(); }}
          >
            ✓
          </button>
        )}
      </div>
      {saving && <span className="admin__comment-saving">saving…</span>}
      {!saving && dirty && <span className="admin__comment-hint">↵ or ✓ to save</span>}
    </div>
  );
};

const AdminPage = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [passwordError, setPasswordError] = useState(false);
  const [activeTab, setActiveTab] = useState("surveys");
  const [surveys, setSurveys] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [previewFile, setPreviewFile] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [savingComment, setSavingComment] = useState(null);

  const handleSaveComment = async (collectionName, id, comment) => {
    setSavingComment(id);
    try {
      await updateDoc(doc(db, collectionName, id), { adminComment: comment });
      if (collectionName === "surveys") {
        setSurveys((prev) =>
          prev.map((s) => (s.id === id ? { ...s, adminComment: comment } : s))
        );
      } else {
        setContacts((prev) =>
          prev.map((c) => (c.id === id ? { ...c, adminComment: comment } : c))
        );
      }
    } catch (err) {
      console.error("Comment save error:", err);
    } finally {
      setSavingComment(null);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (passwordInput === ADMIN_PASSWORD) {
      setAuthenticated(true);
      setPasswordError(false);
    } else {
      setPasswordError(true);
      setPasswordInput("");
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setFetchError("");
    try {
      const surveysSnap = await getDocs(
        query(collection(db, "surveys"), orderBy("createdAt", "desc"))
      );
      setSurveys(surveysSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

      const contactsSnap = await getDocs(
        query(collection(db, "contactMessages"), orderBy("createdAt", "desc"))
      );
      setContacts(contactsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Firestore fetch error:", err);
      if (err.code === "permission-denied") {
        setFetchError(
          "Permission denied — your Firestore rules are blocking reads. " +
          "Go to Firebase Console → Firestore → Rules and set: allow read, write: if true;"
        );
      } else if (err.code === "unavailable") {
        setFetchError("Firestore is unavailable. Check your internet connection and try again.");
      } else {
        setFetchError(`Error (${err.code}): ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authenticated) fetchData();
  }, [authenticated]);

  const handleDelete = async (collectionName, id) => {
    try {
      await deleteDoc(doc(db, collectionName, id));
      if (collectionName === "surveys") {
        setSurveys((prev) => prev.filter((s) => s.id !== id));
      } else {
        setContacts((prev) => prev.filter((c) => c.id !== id));
      }
    } catch (err) {
      console.error("Delete error:", err);
    } finally {
      setConfirmDelete(null);
    }
  };

  const formatDate = (ts) => {
    if (!ts) return "—";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  const filteredSurveys = surveys.filter((s) =>
    [s.name, s.mobile, s.email, s.service]
      .some((v) => v?.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  const filteredContacts = contacts.filter((c) =>
    [c.name, c.mobile, c.message]
      .some((v) => v?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const servicesCount = surveys.reduce((acc, s) => {
    if (s.service) acc[s.service] = (acc[s.service] || 0) + 1;
    return acc;
  }, {});
  const topService =
    Object.entries(servicesCount).sort((a, b) => b[1] - a[1])[0]?.[0] || "—";
  const withFiles = surveys.filter((s) => s.fileBase64).length;

  // ── Login screen ─────────────────────────────────────────────────────────────
  if (!authenticated) {
    return (
      <div className="admin-login">
        <div className="admin-login__card">
          <div className="admin-login__logo">
            <img src={logo} alt="TechB Energy" className="admin-login__logo-img" />
          </div>
          <h2>Admin Portal</h2>
          <p>Sign in to access the dashboard</p>
          <form onSubmit={handleLogin} className="admin-login__form">
            <input
              type="password"
              placeholder="Enter admin password"
              value={passwordInput}
              onChange={(e) => {
                setPasswordInput(e.target.value);
                setPasswordError(false);
              }}
              className={passwordError ? "error" : ""}
            />
            {passwordError && (
              <span className="admin-login__error">Incorrect password. Try again.</span>
            )}
            <button type="submit">Sign In →</button>
          </form>
        </div>
      </div>
    );
  }

  // ── Dashboard ─────────────────────────────────────────────────────────────────
  return (
    <div className="admin">

      {/* File Preview Modal */}
      {previewFile && (
        <div className="admin-modal" onClick={() => setPreviewFile(null)}>
          <div className="admin-modal__box" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal__header">
              <span>{previewFile.name || "File Preview"}</span>
              <button onClick={() => setPreviewFile(null)}>✕</button>
            </div>
            {previewFile.base64.startsWith("data:image") ? (
              <img src={previewFile.base64} alt="bill" className="admin-modal__img" />
            ) : (
              <iframe src={previewFile.base64} title="PDF Preview" className="admin-modal__pdf" />
            )}
            <div className="admin-modal__footer">
              <a
                href={previewFile.base64}
                download={previewFile.name}
                className="admin-modal__download"
              >
                Download File
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete Modal */}
      {confirmDelete && (
        <div className="admin-modal" onClick={() => setConfirmDelete(null)}>
          <div
            className="admin-modal__box admin-modal__box--sm"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Delete this record?</h3>
            <p>This action cannot be undone.</p>
            <div className="admin-modal__actions">
              <button className="btn-secondary" onClick={() => setConfirmDelete(null)}>
                Cancel
              </button>
              <button
                className="btn-danger"
                onClick={() => handleDelete(confirmDelete.col, confirmDelete.id)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Header ── */}
      <header className="admin__header">
        <a href="/" className="admin__header-logo">
          <img src={logo} alt="TechB Energy" className="admin__header-logo-img" />
        </a>
        <div className="admin__header-center">
          <span className="admin__header-badge">Admin Dashboard</span>
        </div>
        <div className="admin__header-right">
          <button className="admin__refresh" onClick={fetchData}>↻ Refresh</button>
          <button className="admin__logout" onClick={() => setAuthenticated(false)}>Sign Out</button>
        </div>
      </header>

      <main className="admin__main">

        {fetchError && (
          <div className="admin__error-banner">
            <strong>Failed to load data:</strong> {fetchError}
            <button onClick={fetchData} className="admin__error-retry">Retry</button>
          </div>
        )}

        {/* Stats */}
        <div className="admin__stats">
          <div className="admin__stat">
            <span className="admin__stat-label">Total Surveys</span>
            <span className="admin__stat-value">{surveys.length}</span>
          </div>
          <div className="admin__stat">
            <span className="admin__stat-label">Total Messages</span>
            <span className="admin__stat-value">{contacts.length}</span>
          </div>
          <div className="admin__stat">
            <span className="admin__stat-label">Bills Uploaded</span>
            <span className="admin__stat-value">{withFiles}</span>
          </div>
          <div className="admin__stat">
            <span className="admin__stat-label">Top Service</span>
            <span className="admin__stat-value admin__stat-value--sm">
              {topService === "—" ? "—" : topService.split(" ").slice(0, 2).join(" ")}
            </span>
          </div>
        </div>

        {/* Tabs + Search */}
        <div className="admin__toolbar">
          <div className="admin__tabs">
            <button
              className={`admin__tab ${activeTab === "surveys" ? "active" : ""}`}
              onClick={() => { setActiveTab("surveys"); setSearchTerm(""); }}
            >
              Survey Requests
              <span className="admin__tab-badge">{surveys.length}</span>
            </button>
            <button
              className={`admin__tab ${activeTab === "contacts" ? "active" : ""}`}
              onClick={() => { setActiveTab("contacts"); setSearchTerm(""); }}
            >
              Contact Messages
              <span className="admin__tab-badge">{contacts.length}</span>
            </button>
          </div>
          <input
            type="text"
            className="admin__search"
            placeholder="Search name, mobile, email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {loading ? (
          <div className="admin__loading">Loading data...</div>
        ) : (
          <>
            {/* Surveys Table */}
            {activeTab === "surveys" && (
              <div className="admin__table-wrap">
                {filteredSurveys.length === 0 ? (
                  <div className="admin__empty">No survey requests found.</div>
                ) : (
                  <table className="admin__table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Mobile</th>
                        <th>Email</th>
                        <th>Service</th>
                        <th>Bill</th>
                        <th>Comment</th>
                        <th>Date</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSurveys.map((s, i) => (
                        <tr key={s.id}>
                          <td className="admin__td-num">{i + 1}</td>
                          <td data-label="Name"><strong>{s.name}</strong></td>
                          <td data-label="Mobile">
                            <a href={`tel:${s.mobile}`} className="admin__link">{s.mobile}</a>
                          </td>
                          <td data-label="Email">{s.email || <span className="admin__muted">—</span>}</td>
                          <td data-label="Service">
                            {s.service
                              ? <span className="admin__badge">{s.service}</span>
                              : <span className="admin__muted">—</span>}
                          </td>
                          <td data-label="Bill">
                            {s.fileBase64 ? (
                              <button
                                className="admin__view-btn"
                                onClick={() => setPreviewFile({ base64: s.fileBase64, name: s.fileName })}
                              >
                                View Bill
                              </button>
                            ) : (
                              <span className="admin__muted">None</span>
                            )}
                          </td>
                          <td data-label="Comment">
                            <CommentCell
                              id={s.id}
                              collectionName="surveys"
                              value={s.adminComment || ""}
                              saving={savingComment === s.id}
                              onSave={handleSaveComment}
                            />
                          </td>
                          <td data-label="Date" className="admin__muted">{formatDate(s.createdAt)}</td>
                          <td>
                            <button
                              className="admin__del-btn"
                              onClick={() => setConfirmDelete({ col: "surveys", id: s.id })}
                              title="Delete"
                            >✕</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* Contacts Table */}
            {activeTab === "contacts" && (
              <div className="admin__table-wrap">
                {filteredContacts.length === 0 ? (
                  <div className="admin__empty">No contact messages found.</div>
                ) : (
                  <table className="admin__table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Mobile</th>
                        <th>Message</th>
                        <th>Comment</th>
                        <th>Date</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredContacts.map((c, i) => (
                        <tr key={c.id}>
                          <td className="admin__td-num">{i + 1}</td>
                          <td data-label="Name"><strong>{c.name}</strong></td>
                          <td data-label="Mobile">
                            <a href={`tel:${c.mobile}`} className="admin__link">{c.mobile}</a>
                          </td>
                          <td data-label="Message" className="admin__msg">{c.message}</td>
                          <td data-label="Comment">
                            <CommentCell
                              id={c.id}
                              collectionName="contactMessages"
                              value={c.adminComment || ""}
                              saving={savingComment === c.id}
                              onSave={handleSaveComment}
                            />
                          </td>
                          <td data-label="Date" className="admin__muted">{formatDate(c.createdAt)}</td>
                          <td>
                            <button
                              className="admin__del-btn"
                              onClick={() => setConfirmDelete({ col: "contactMessages", id: c.id })}
                              title="Delete"
                            >✕</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default AdminPage;